import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import MenuItem from 'apollo-react/components/MenuItem';
import Typography from '../../common/StyledTypography';
import { toast } from 'react-toastify';
import Select from 'apollo-react/components/Select';
import Switch from 'apollo-react/components/Switch';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Grid from 'apollo-react/components/Grid';
import { useTranslation } from 'react-i18next';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import { ROLE_STATUS } from '../../../constants/role.constant';

const NotificationConfigList = () => {
  const { t, ready, i18n } = useTranslation();

  useEffect(() => {}, [i18n.language]);

  const staticEvents = [
    { label: t('notificationConfig.userCreate'), value: 'user_create' },
    {
      label: t('notificationConfig.roleAndPermissionsUpdate'),
      value: 'role_and_permissions_update',
    },
    {
      label: t('notificationConfig.workflowCreate'),
      value: 'workflow_create',
    },
    {
      label: t('notificationConfig.formCreate'),
      value: 'form_create',
    },
    {
      label: t('notificationConfig.formUpdate'),
      value: 'form_update',
    },
    {
      label: t('notificationConfig.workflowUpdate'),
      value: 'workflow_update',
    },
  ];

  const channelOptions = [
    { label: t('notificationConfig.inApp'), value: 'InApp' },
  ];

  const channelMap = {
    1: 'InApp',
  };

  const [data, setData] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationParams, setPaginationParams] = useState({
    limit: 6,
    offset: 0,
    sortField: null,
    sortOrder: null,
  });

  const navigate = useNavigate();

  useEffect(() => {
    // fire both in parallel
    Promise.all([fetchAdmins(), fetchConfigs()]).finally(() =>
      setLoading(false)
    );
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await axiosInstance.get(`/api/roles`, {
        params: {
          status: ROLE_STATUS.PUBLISHED_ACTIVE,
        },
      });
      setAdmins(res.data.data || []);
    } catch (err) {
      console.error('Failed to load admins', err);
    }
  };

  const fetchConfigs = async () => {
    try {
      const res = await axiosInstance.get(`/api/user/notifications-trigger`);
      const configs = res.data;

      const byEvent = configs.reduce((acc, row) => {
        const key = row.TriggerEvent?.name;
        if (!acc[key]) acc[key] = [];
        acc[key].push(row);
        return acc;
      }, {});
      console.log('Grouped notification configs by event:', byEvent);

      const merged = staticEvents.map((evt) => {
        const list = byEvent[evt.value] || [];

        const channels =
          list.length > 0
            ? [...new Set(list.map((r) => channelMap[r.notification_type_id]))]
            : ['InApp'];

        return {
          id: list[0]?.id ?? evt.value,
          event: evt.value,
          recipients: list.map((r) => r.role_id),
          channels: channels,
          enabled: list.some((r) => r.status === true),
        };
      });
      console.log('Fetched notification configs:', merged);

      setData(merged);
      setInitialData(JSON.parse(JSON.stringify(merged)));
    } catch (err) {
      console.error(err);
      const fallback = staticEvents.map((evt) => ({
        id: evt.value,
        event: evt.value,
        recipients: [],
        channels: ['InApp'],
        enabled: false,
      }));
      setData(fallback);
      setInitialData(JSON.parse(JSON.stringify(fallback)));
    } finally {
      setLoading(false);
    }
  };

  const updateField = (id, field, value) => {
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleSave = async (row) => {
    console.log('Saving row:', row);

    if (!row.recipients || row.recipients.length === 0) {
      toast.error(t('notificationConfig.validationErrorRecipients'));
      return;
    }

    if (!row.channels || row.channels.length === 0) {
      toast.error(t('notificationConfig.validationErrorChannels'));
      return;
    }

    try {
      await axiosInstance.post(`/api/user/notifications-trigger`, [
        {
          event: row.event,
          recipients: row.recipients,
          channels: row.channels,
          enabled: row.enabled,
        },
      ]);
      toast.success(t('notificationConfig.saveSuccess'));
      await fetchConfigs();
    } catch (err) {
      toast.error(t('notificationConfig.saveError'));
      console.error('Save failed', err);
    }
  };

  const handleCancel = () => {
    fetchConfigs();
  };

  const isDirty = useMemo(() => {
    if (data.length === 0 || initialData.length === 0) return false;
    return data.some((row, i) => {
      const init = initialData[i];
      if (!init) return true;
      const recipientsMatch =
        JSON.stringify([...(row.recipients || [])].sort()) ===
        JSON.stringify([...(init.recipients || [])].sort());
      const channelsMatch =
        JSON.stringify([...(row.channels || [])].sort()) ===
        JSON.stringify([...(init.channels || [])].sort());
      return !recipientsMatch || !channelsMatch || row.enabled !== init.enabled;
    });
  }, [data, initialData]);

  useUnsavedChangesWarning(isDirty);

  const handleSaveAll = useCallback(async () => {
    const dirtyRows = data.filter((row, i) => {
      const init = initialData[i];
      if (!init) return true;
      const recipientsMatch =
        JSON.stringify([...(row.recipients || [])].sort()) ===
        JSON.stringify([...(init.recipients || [])].sort());
      const channelsMatch =
        JSON.stringify([...(row.channels || [])].sort()) ===
        JSON.stringify([...(init.channels || [])].sort());
      return !recipientsMatch || !channelsMatch || row.enabled !== init.enabled;
    });

    for (const row of dirtyRows) {
      if (!row.recipients || row.recipients.length === 0) {
        toast.error(t('notificationConfig.validationErrorRecipients'));
        return false;
      }
      if (!row.channels || row.channels.length === 0) {
        toast.error(t('notificationConfig.validationErrorChannels'));
        return false;
      }
    }

    for (const row of dirtyRows) {
      try {
        await axiosInstance.post(`/api/user/notifications-trigger`, [
          {
            event: row.event,
            recipients: row.recipients,
            channels: row.channels,
            enabled: row.enabled,
          },
        ]);
      } catch (err) {
        toast.error(t('notificationConfig.saveError'));
        console.error('Save failed', err);
        return false;
      }
    }

    toast.success(t('notificationConfig.saveSuccess'));
    await fetchConfigs();
  }, [data, initialData, t]);

  const columns = [
    {
      field: 'event',
      header: t('notificationConfig.triggerEvent'),
      body: (row) => {
        const match = staticEvents.find(
          (option) => option.value === (row.event || '')
        );
        const label = match ? match.label : '—';
        return (
          <Typography variant="bodyDefault" component="span">
            {label}
          </Typography>
        );
      },
    },
    {
      field: 'recipients',
      header: t('notificationConfig.recipients'),
      body: (row) => (
        <Select
          multiple
          value={row.recipients}
          onChange={(e) => updateField(row.id, 'recipients', e.target.value)}
          placeholder={t('notificationConfig.selectRoles')}
          style={{ width: 200 }}
        >
          <MenuItem isTitle>{t('notificationConfig.admins')}</MenuItem>
          {admins.map((admin) => (
            <MenuItem key={admin.id} value={admin.id}>
              {admin.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'channels',
      header: t('notificationConfig.channels'),
      body: (row) => (
        <Select
          value={row.channels}
          onChange={(e) => updateField(row.id, 'channels', e.target.value)}
          multiple
          placeholder={t('notificationConfig.selectChannels')}
          style={{ width: 200 }}
        >
          <MenuItem isTitle>{t('notificationConfig.channels')}</MenuItem>
          {channelOptions.map((ch) => (
            <MenuItem key={ch.value} value={ch.value}>
              {ch.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'status',
      header: t('notificationConfig.enabled'),
      body: (row) => (
        <Switch
          checked={!!row.enabled}
          onChange={(e, checked) => updateField(row.id, 'enabled', checked)}
          size="small"
        />
      ),
    },
  ];

  const actions = [
    { text: t('save'), color: 'primary', onClick: handleSave },
    { text: t('cancel'), color: 'secondary', onClick: handleCancel },
  ];

  if (!ready) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      <DataTableComponent
        data={data}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={data.length}
        onDataTableEvent={(evt) => {
          setPaginationParams({
            ...paginationParams,
            limit: evt.rows,
            offset: evt.first,
            sortField: evt.sortField,
            sortOrder: evt.sortOrder,
          });
        }}
        enablePagination={false}
        enableExport={false}
        enableCreate={false}
        enableLazyLoading={false}
        sectionName="Notification-Triggers"
        actions={actions}
      />
    </div>
  );
};

export default NotificationConfigList;
